<template>
  <v-row>
    <v-col>
      <v-container fluid>
        <v-row>
          <v-col class="d-flex flex-row align-center">
            <v-btn text @click="()=>{this.$router.go(-1)}"><v-icon dark>mdi-arrow-left</v-icon></v-btn>
            <h1>{{plural}}</h1>
            <v-btn class="ml-2" small fab color="info" @click="newForm"><v-icon>mdi-plus</v-icon></v-btn>
            <v-progress-circular indeterminate color="green" v-if="dataLoader" style="margin-left: 10px;"></v-progress-circular>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="6">
            <v-alert dense text type="info">
              A product that is <b>Used In Multiples</b> can pick a conversion. Its quantities are stored in the <b>Unit</b>, and the order line item shows one field per row below
              (e.g. <b>QTY x ft in</b>). The line's real quantity is QTY x the sum of every field converted to the unit.
              Use the smallest unit (e.g. inches) as the Unit so every field is a whole number of it and stock never has repeating decimals.
            </v-alert>
            <v-alert dense text type="warning" v-if="lowPrecision">
              The <b>quantityPrecision</b> global is {{precision}} decimal place(s). Quantities are rounded to that precision, so a field like 4 in (0.3333 ft) will not be exact.
              Set it to 3 or more for conversions that split a unit (e.g. ft into in).
            </v-alert>
            <span class="d-flex flex-row">
              <h3 v-if="isCreate">Create {{singular}}</h3>
              <h3 v-else>Edit ID:{{form.id}} | {{form.label}}</h3>
            </span>
            <span v-if="!isCreate" class="d-flex flex-row mb-2">
              <v-btn v-if="!isEditable" @click="isEditable=!isEditable">Edit</v-btn>
              <v-btn v-else @click="cancelForm">Cancel</v-btn>
              <v-btn v-if="isEditable" class="ml-2" color="error" :loading="formLoader" @click="deleteData">Delete</v-btn>
            </span>
            <v-form :disabled="!isEditable && !isCreate" v-model="validForm">
              <v-text-field :rules="requiredRules" outlined label="Unit (the product's quantities are stored in this)" v-model="form.unit" :disabled="!isCreate" hint="e.g. ft. Cannot be changed after creating." persistent-hint class="mb-2"/>

              <v-select dense outlined label="Unit price is per" v-model="form.priceUnit" :items="priceUnitOptions" item-text="text" item-value="value" persistent-hint class="mb-4"
                        hint="The product's unit price is quoted in this unit (e.g. $ per ft) while stock is kept in the Unit (e.g. in). The product's 'Charge by the whole ...' checkbox then rounds the line up to whole price units."/>

              <v-text-field dense outlined label="Minimum order quantity" type="number" v-model.number="form.minSaleQuantity" persistent-hint class="mb-4"
                            :hint="`In ${form.unit || 'unit'}. Set on a product when this conversion is picked on it; it can still be changed on the product afterwards.`"/>

              <h3 class="mb-2">Fields shown on the order line item</h3>
              <v-card outlined v-for="(field, i) of form.fields" :key="i" class="mb-3">
                <v-card-text>
                  <div class="d-flex flex-row align-center flex-wrap" style="gap: 8px;">
                    <v-text-field :rules="requiredRules" dense outlined hide-details label="Label" v-model="field.label" style="max-width: 110px;"/>
                    <v-text-field dense outlined hide-details label="Print label (optional)" :placeholder="field.label" v-model="field.printLabel" style="max-width: 170px;"/>
                    <span>1 {{field.label || '?'}} =</span>
                    <v-text-field :rules="positiveIntRules" dense outlined hide-details label="Amount" type="number" min="1" v-model.number="field.num" style="max-width: 100px;"/>
                    <span>÷</span>
                    <v-text-field :rules="positiveIntRules" dense outlined hide-details label="Per" type="number" min="1" v-model.number="field.den" style="max-width: 100px;"/>
                    <span>{{form.unit || 'unit'}}</span>
                    <v-spacer/>
                    <v-btn icon small color="error" :disabled="form.fields.length<2" @click="form.fields.splice(i, 1)"><v-icon>mdi-delete</v-icon></v-btn>
                  </div>
                  <div class="d-flex flex-row align-center flex-wrap mt-3" style="gap: 8px;">
                    <v-text-field dense outlined hide-details label="Min" type="number" v-model.number="field.min" style="max-width: 90px;"/>
                    <v-text-field dense outlined hide-details label="Max (blank = no limit)" type="number" v-model="field.max" style="max-width: 170px;"/>
                    <v-text-field dense outlined hide-details label="Step" type="number" v-model.number="field.step" style="max-width: 90px;"/>
                    <v-text-field dense outlined hide-details label="Default" type="number" v-model.number="field.default" style="max-width: 90px;"/>
                  </div>
                </v-card-text>
              </v-card>
              <v-btn small color="info" class="mb-3" @click="addField"><v-icon>mdi-plus</v-icon> Field</v-btn>

              <v-alert dense text type="success" v-if="preview">
                <div><b>{{preview.label}}</b></div>
                <div>Order line: <b>QTY x {{form.fields.map(x=>x.label).join(' ')}}</b>, stock in {{form.unit}}, price per {{form.priceUnit || form.unit}}</div>
                <div v-if="preview.requiresFractional">Products using this will automatically allow fractional quantities.</div>
              </v-alert>
            </v-form>
            <span v-if="isEditable && !isCreate" class="d-flex flex-row">
              <v-btn :disabled="!validForm" class="ml-1" color="success" @click="updateData" :loading="formLoader">Update</v-btn>
            </span>
            <span v-if="isCreate" class="d-flex flex-row">
              <v-btn :disabled="!validForm" class="ml-1" color="success" @click="createData" :loading="formLoader">Save</v-btn>
            </span>
            <span v-if="!isCreate" class="d-flex flex-column mt-6">
              <v-btn color="warning" :disabled="isEditable" @click="openApplyDialog">Update all products using this conversion</v-btn>
              <span v-if="isEditable" class="caption mt-1">Save your changes to the conversion first.</span>
            </span>
          </v-col>
          <v-col cols="12" lg="6">
            <v-data-table mobile-breakpoint="0" :items="data" :headers="headers" :items-per-page="-1" @click:row="rowClick" :loading="dataLoader"/>
          </v-col>
        </v-row>
      </v-container>

      <v-dialog v-model="applyDialog.isOpen" max-width="700" scrollable>
        <v-card>
          <v-card-title>Update products using {{form.label}}</v-card-title>
          <v-card-text>
            <v-progress-linear v-if="applyDialog.loading" indeterminate/>
            <div v-else>
              <p v-if="applyDialog.products.length===0">No products use this conversion.</p>
              <div v-else>
                <p><b>{{applyDialog.products.length}}</b> product(s) will be updated.</p>
                <p class="caption">
                  Each product will be set to: unit <b>{{form.unit}}</b>, factor of quantity <b>{{applyDialog.step}}</b>,
                  minimum order quantity <b>{{form.minSaleQuantity==null ? '(unchanged)' : form.minSaleQuantity}}</b>{{applyDialog.requiresFractional ? ', and fractional quantities enabled' : ''}}.
                  Any custom values on those products for these settings will be overwritten.
                </p>
                <v-data-table dense hide-default-footer :items-per-page="-1" :items="applyDialog.products" :headers="applyHeaders" style="max-height: 360px; overflow-y: auto;"/>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pl-5 pb-5">
            <v-btn text color="error" @click="applyDialog.isOpen=false">Cancel</v-btn>
            <v-btn color="success" :disabled="applyDialog.loading || applyDialog.products.length===0" :loading="applyDialog.saving" @click="confirmApply">Confirm update</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import axios from "axios";

const blankField = () => ({label: '', printLabel: '', num: 1, den: 1, min: 0, max: null, step: 1, default: 0});

export default {
  name: 'UnitConversions',
  data () {
    return {
      dataLoader: false,
      formLoader: false,

      isEditable: false,
      isCreate: true,

      singular: "Unit Conversion",
      plural: "Unit Conversions",

      requiredRules: [
        v => !!v || 'Field is required.'
      ],
      positiveIntRules: [
        v => (Number.isInteger(v) && v>=1) || 'Whole number of 1 or more.'
      ],

      applyDialog: {isOpen: false, loading: false, saving: false, products: [], step: null, requiresFractional: false},
      applyHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'SKU', value: 'sku' }
      ],

      data: [],
      form: {unit: '', priceUnit: null, minSaleQuantity: null, fields: [blankField()]},
      validForm: false,

      headers: [
        { text: 'ID', align: 'start', value: 'id' },
        { text: 'Conversion', value: 'label' },
        { text: 'Price per', value: 'priceUnitLabel' },
        { text: 'MOQ', value: 'minSaleQuantity' },
        { text: 'Fractional', value: 'requiresFractional' }
      ]
    }
  },
  computed: {
    ...mapGetters(['getEndpoint', 'getGlobalValue']),
    precision(){
      return parseInt(this.getGlobalValue('quantityPrecision') || '0');
    },
    lowPrecision(){
      return this.precision < 3 && this.data.some(x => x.requiresFractional);
    },
    priceUnitOptions(){
      return [{text: `${this.form.unit || 'unit'} (the base unit)`, value: null}, ...(this.form.fields || []).filter(x => x.label).map(x => ({text: x.label, value: x.label}))];
    },
    preview(){
      if(!this.form.unit || !this.form.fields?.length) return null;
      let label = `Unit: ${this.form.unit} | Show: ${this.form.fields.map(x=>x.label).join(', ')} (charged by ${this.form.priceUnit || this.form.unit})`;
      let requiresFractional = this.form.fields.some(x => (Number(x.num) % Number(x.den)) !== 0 || !Number.isInteger(Number(x.step)));
      return {label, requiresFractional};
    }
  },
  async mounted(){
    await this.fetchData();
  },
  methods: {
    ...mapMutations(['setUnitConversions']),
    clone(row){
      let copy = JSON.parse(JSON.stringify({id: row.id, unit: row.unit, priceUnit: row.priceUnit || null, minSaleQuantity: row.minSaleQuantity, label: row.label, fields: row.fields}));
      // a print label identical to the label is just the default, so show it as blank
      copy.fields.forEach(f => { if(f.printLabel === f.label) f.printLabel = ''; });
      return copy;
    },
    newForm(){
      this.form = {unit: '', priceUnit: null, minSaleQuantity: null, fields: [blankField()]};
      this.isEditable = true;
      this.isCreate = true;
    },
    addField(){
      this.form.fields.push(blankField());
    },
    rowClick(row){
      this.form = this.clone(row);
      this.isEditable = false;
      this.isCreate = false;
    },
    cancelForm(){
      this.form = this.clone(this.data.find(x=>x.id===this.form.id));
      this.isEditable = false;
    },
    async fetchData(){
      try{
        this.dataLoader = true;

        let res = await axios.get(`${this.getEndpoint}/api/unitconversions`)
        this.data = res.data.data.map(x => ({...x, priceUnitLabel: x.priceUnit || x.unit}))
        // keeps the product/order screens' cache in step with the edits made here
        this.setUnitConversions(res.data.data)
      }
      finally {
        this.dataLoader = false;
      }
    },
    payload(){
      return {
        unit: this.form.unit,
        priceUnit: this.form.priceUnit || null,
        minSaleQuantity: this.form.minSaleQuantity === '' ? null : this.form.minSaleQuantity,
        fields: this.form.fields.map(x => ({...x, max: (x.max===null || x.max==='' || x.max===undefined) ? null : Number(x.max)}))
      };
    },
    async createData(){
      try{
        this.formLoader = true;
        await axios.post(`${this.getEndpoint}/api/unitconversions`, this.payload())
        this.$snack(`${this.singular} created!`, "success");
        await this.fetchData();
        this.newForm();
      }
      finally {
        this.formLoader = false;
      }
    },
    async updateData(){
      try{
        this.formLoader = true;
        await axios.put(`${this.getEndpoint}/api/unitconversions/${this.form.id}`, this.payload())
        this.$snack(`${this.singular} updated!`, "success");
        await this.fetchData();
        this.isEditable = false;
      }
      finally {
        this.formLoader = false;
      }
    },
    async openApplyDialog(){
      try{
        let current = this.data.find(x => x.id === this.form.id);
        this.applyDialog = {isOpen: true, loading: true, saving: false, products: [], step: current?.quantityStep, requiresFractional: !!current?.requiresFractional};
        let res = await axios.get(`${this.getEndpoint}/api/unitconversions/${this.form.id}/products`)
        this.applyDialog.products = res.data.data
      }
      finally {
        this.applyDialog.loading = false;
      }
    },
    async confirmApply(){
      try{
        this.applyDialog.saving = true;
        let res = await axios.post(`${this.getEndpoint}/api/unitconversions/${this.form.id}/applyToProducts`)
        this.$snack(`${res.data.updated} product(s) updated!`, "success");
        this.applyDialog.isOpen = false;
      }
      finally {
        this.applyDialog.saving = false;
      }
    },
    async deleteData(){
      if(!confirm(`Delete ${this.form.label}?`)) return;
      try{
        this.formLoader = true;
        await axios.delete(`${this.getEndpoint}/api/unitconversions/${this.form.id}`)
        this.$snack(`${this.singular} deleted!`, "success");
        await this.fetchData();
        this.newForm();
      }
      finally {
        this.formLoader = false;
      }
    }
  }
}
</script>
